import { computed, type ComputedRef } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { getArticleDetails } from '@/common/api/api.pleased';
import type { ArticleDetail } from '@/common/api/models.pleased';
import { useMockedFlowStore } from '@/library/cf-v1/mocked-flow';
import { storeToRefs } from 'pinia';
import { devLog } from '@/common/debug';

/**
 * TanStack Query hook to fetch full article details including config
 * Provides automatic caching, loading states, and error handling
 *
 * @param articleId - Article ID to fetch details for
 * @returns TanStack Query result with full article data
 *
 * @example
 * ```ts
 * const articleId = computed(() => Number(route.params.id));
 * const { data: articleData } = useFullArticleDetails(articleId);
 * const articleConfig = computed(() => articleData.value?.articleConfig);
 * ```
 */
export const useFullArticleDetails = (articleId: ComputedRef<string | number | undefined>) => {
  const mockedFlowStore = useMockedFlowStore();
  const { mockedArticle, hasMockedArticle } = storeToRefs(mockedFlowStore);

  return useQuery({
    queryKey: ['fullArticleDetails', articleId],
    queryFn: async (): Promise<ArticleDetail> => {
      if (!articleId.value) {
        throw new Error('Article ID is required');
      }

      // Return mocked article data if available in mocked flow
      if (hasMockedArticle.value && mockedArticle.value) {
        devLog.debug('[ARTICLE_DETAILS_API] Using mocked article data', mockedArticle.value);
        return mockedArticle.value;
      }

      // Otherwise fetch from API
      const response = await getArticleDetails.cached({ articleId: articleId.value });
      return response.data;
    },
    enabled: computed(() => !!articleId.value),
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    retry: true,
    retryDelay: (attempt) => Math.min(2000 * 2 ** attempt, 60_000),
  });
};
